import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { Box } from '@mui/material';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

import {
  normalizeVideoEmbedUrl,
  sanitizeMedicalNoteHtml,
  toQuillHtml,
} from 'utils/medicalNoteContent';

import './quillEditor.css';
import { registerQuillDivider } from './quillDividerBlot';
import {
  DIVIDER_KEYBOARD_BINDINGS,
  insertQuillDivider,
} from './quillDividerHandlers';
import { QUILL_FONT_WHITELIST, registerQuillFonts } from './quillFontConfig';
import { installPreserveFormatsOnEnter } from './quillPreserveFormatsOnEnter';

registerQuillDivider();
registerQuillFonts();

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, false] }],
  [{ font: [...QUILL_FONT_WHITELIST] }],
  [{ color: [] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['divider'],
  ['image', 'video'],
  ['clean'],
];

const EMPTY_EDITOR_HTML = '<p><br></p>';

const getEditorHtml = (quill: Quill): string => {
  // getSemanticHTML()는 Quill video blot을 <a> 링크로 변환하므로 iframe을 유지하려면 root HTML 사용
  return sanitizeMedicalNoteHtml(quill.root.innerHTML);
};

export interface QuillEditorHandle {
  focus: () => void;
}

interface Props {
  defaultValue?: string;
  onChange: (html: string) => void;
  placeholder?: string;
  onImageUpload?: (file: File) => Promise<string | null>;
  onVideoError?: (message: string) => void;
  disabled?: boolean;
}

const QuillEditor = forwardRef<QuillEditorHandle, Props>(
  (
    {
      defaultValue = '',
      onChange,
      placeholder = '환자상황 및 처방내역 내용을 입력해주세요.',
      onImageUpload,
      onVideoError,
      disabled = false,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const onChangeRef = useRef(onChange);
    const onImageUploadRef = useRef(onImageUpload);
    const onVideoErrorRef = useRef(onVideoError);
    const isApplyingInitialContentRef = useRef(true);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      onImageUploadRef.current = onImageUpload;
    }, [onImageUpload]);

    useEffect(() => {
      onVideoErrorRef.current = onVideoError;
    }, [onVideoError]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        quillRef.current?.focus();
      },
    }));

    useEffect(() => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const editorContainer = container.ownerDocument.createElement('div');
      container.appendChild(editorContainer);

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        placeholder,
        modules: {
          keyboard: {
            bindings: DIVIDER_KEYBOARD_BINDINGS,
          },
          toolbar: {
            container: TOOLBAR_OPTIONS,
            handlers: {
              divider(this: { quill: Quill }) {
                insertQuillDivider(this.quill);
              },
              image: () => {
                fileInputRef.current?.click();
              },
              video(this: { quill: Quill }) {
                const url = window.prompt(
                  '동영상 URL을 입력해주세요. (YouTube, Vimeo, mp4)',
                );
                if (!url) return;

                const embedUrl = normalizeVideoEmbedUrl(url);
                if (!embedUrl) {
                  onVideoErrorRef.current?.(
                    '지원하지 않는 동영상 URL입니다. YouTube, Vimeo, mp4 링크만 사용할 수 있습니다.',
                  );
                  return;
                }

                const range = this.quill.getSelection(true);
                const index = range?.index ?? this.quill.getLength();
                this.quill.insertEmbed(index, 'video', embedUrl);
                this.quill.setSelection(index + 1);
              },
            },
          },
        },
      });

      quillRef.current = quill;
      installPreserveFormatsOnEnter(quill);

      const handleTextChange = () => {
        if (isApplyingInitialContentRef.current) return;
        onChangeRef.current(getEditorHtml(quill));
      };

      quill.on('text-change', handleTextChange);

      isApplyingInitialContentRef.current = true;
      const initialHtml = toQuillHtml(defaultValue);
      quill.clipboard.dangerouslyPasteHTML(
        initialHtml || EMPTY_EDITOR_HTML,
        'silent',
      );
      window.requestAnimationFrame(() => {
        isApplyingInitialContentRef.current = false;
      });

      return () => {
        quill.off('text-change', handleTextChange);
        quillRef.current = null;
        container.innerHTML = '';
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placeholder]);

    useEffect(() => {
      quillRef.current?.enable(!disabled);
    }, [disabled]);

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file = event.target.files?.[0];
      event.target.value = '';

      if (!file || !onImageUploadRef.current || !quillRef.current) return;

      const url = await onImageUploadRef.current(file);
      if (!url) return;

      const quill = quillRef.current;
      const range = quill.getSelection(true);
      const index = range?.index ?? quill.getLength();
      quill.insertEmbed(index, 'image', url);
      quill.setSelection(index + 1);
    };

    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />
        <Box
          className="quill-editor-root"
          ref={containerRef}
          sx={{
            opacity: disabled ? 0.6 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
          }}
        />
      </>
    );
  },
);

QuillEditor.displayName = 'QuillEditor';

export default QuillEditor;
